import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { axiosServiceInstance } from '@onesource/services';
import { Link, useLocation } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { MdChevronLeft } from 'react-icons/md';
import { RiExternalLinkLine, RiFolderLine, RiFolderOpenFill, RiLockLine, RiLockUnlockLine } from 'react-icons/ri';
import { match } from 'ts-pattern';

import type { SidebarItem, SidebarSubItem } from './types';

/**
 * A folder should open when the active route is (or lives under) one of its
 * children — i.e. the parent space of an open child space.
 */
const containsActiveRoute = (child: SidebarSubItem, pathname: string): boolean =>
  match(child)
    .when(
      c => !!c.path && pathname.startsWith(c.path),
      () => true,
    )
    .when(
      c => (c.children ?? []).some(grandChild => !!grandChild.path && pathname.startsWith(grandChild.path)),
      () => true,
    )
    .otherwise(() => false);

interface OttGenerationResponse {
  ott: string;
  redirectUrl: string;
  expiresIn: number;
}

interface SubmenuPanelProps {
  item: SidebarItem;
  // Omitted when the collapsible-sidebar feature is off — the collapse button is
  // hidden and the panel stays permanently expanded.
  onCollapse?: () => void;
}

/**
 * The clickable row content for a tree node — a space / sub-space link, an
 * external app launcher (OTT), or a plain label. Rendered as the TreeItem `label`.
 */
const SubItemLabel = ({ subItem }: { subItem: SidebarSubItem }) => {
  const location = useLocation();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);

  const isActive = subItem.path ? location.pathname.startsWith(subItem.path) : false;
  const Icon = subItem.icon;

  const handleExternalAppClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!subItem.externalApp || loading) {
      return;
    }

    setLoading(true);
    try {
      const response = await axiosServiceInstance.post<OttGenerationResponse>('/auth/generate-ott/user', {
        targetApp: subItem.externalApp.appId,
        redirectUrl: subItem.externalApp.url,
      });
      const anchor = document.createElement('a');
      anchor.href = response.data.redirectUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch (error) {
      console.error('Failed to generate OTT:', error);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {Icon && (
          <Box
            component="span"
            sx={{ display: 'flex', flexShrink: 0, color: subItem.isLocked ? '#B3B3B3' : 'grey.600' }}
          >
            <Icon size={14} />
          </Box>
        )}
        <Typography
          component="span"
          noWrap
          sx={{
            fontSize: 12,
            fontWeight: isActive ? 700 : 400,
            color: subItem.isLocked ? '#B3B3B3' : '#0F0F0F',
          }}
          dangerouslySetInnerHTML={{ __html: subItem.label }}
        />
      </Box>
      {loading ? (
        <CircularProgress size={12} sx={{ ml: 1 }} />
      ) : subItem.externalApp ? (
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
          <RiExternalLinkLine size={12} color={theme.palette.grey[900]} />
        </Box>
      ) : (
        subItem.isLocked !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            {subItem.isLocked ? (
              <RiLockLine size={12} color={theme.palette.grey[900]} />
            ) : (
              <RiLockUnlockLine size={12} color={theme.palette.grey[900]} />
            )}
          </Box>
        )
      )}
    </Box>
  );

  // External apps launch via OTT (no route); internal paths navigate via Link.
  if (subItem.externalApp) {
    return (
      <Box onClick={handleExternalAppClick} sx={{ cursor: loading ? 'default' : 'pointer' }}>
        {content}
      </Box>
    );
  }

  if (subItem.path) {
    return (
      <Link to={subItem.path} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        {content}
      </Link>
    );
  }

  return content;
};

// Width of the horizontal connector tick; doubles as the group's left padding so
// the tick spans exactly from the vertical guide line to the row.
const CONNECTOR_WIDTH = 10;

// The tick joining the group's vertical guide line to a nested row (forms an L
// with it). Only rows inside a group — depth > 0 — get one.
const connectorSx = {
  content: '""',
  position: 'absolute',
  left: -CONNECTOR_WIDTH,
  top: '50%',
  width: CONNECTOR_WIDTH,
  height: '1px',
  bgcolor: 'divider',
} as const;

/**
 * Recursively render a sidebar sub-item (and any nested sub-spaces) as a tree node.
 */
const renderTreeItem = (subItem: SidebarSubItem, depth = 0) => {
  const children = subItem.children ?? [];

  return (
    <TreeItem
      key={subItem.id}
      nodeId={subItem.id}
      label={<SubItemLabel subItem={subItem} />}
      sx={{
        '& > .MuiTreeItem-content': {
          position: 'relative',
          py: 0,
          px: 0.5,
          borderRadius: '5px',
          '&:hover': { bgcolor: 'action.hover' },
          '&.Mui-selected, &.Mui-selected.Mui-focused, &.Mui-selected:hover': {
            bgcolor: '#FFE9DB66',
          },
          ...(depth > 0 && { '&::before': connectorSx }),
        },
        '& .MuiTreeItem-iconContainer': {
          width: 16,
          color: 'grey.700',
        },
      }}
    >
      {children.map(child => renderTreeItem(child, depth + 1))}
    </TreeItem>
  );
};

export const SubmenuPanel = ({ item, onCollapse }: SubmenuPanelProps) => {
  const location = useLocation();
  const children = useMemo(() => item.children ?? [], [item.children]);

  // Folders whose (grand)child is the active route — the parent space of the
  // open child space.
  const activeParentIds = useMemo(
    () => children.filter(child => containsActiveRoute(child, location.pathname)).map(child => child.id),
    [children, location.pathname],
  );

  // Controlled expansion: auto-open the parent whenever navigation lands on one
  // of its children, while preserving folders the user opened themselves.
  const [expanded, setExpanded] = useState<string[]>(activeParentIds);
  useEffect(() => {
    setExpanded(prev => [...new Set([...prev, ...activeParentIds])]);
  }, [activeParentIds]);

  if (children.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        ml: 0.3,
        position: 'absolute',
        left: 70,
        top: 0,
        width: 245,
        minHeight: '100%',
        bgcolor: '#fafafa',
        borderRadius: '0 20px 0 0',
        boxShadow: '14px 17px 40px 0px rgba(112,144,176,0.08)',
        py: 4.75,
        px: 1.5,
        zIndex: 10,
      }}
    >
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          component="span"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: '#0F0F0F',
            bgcolor: '#FFEEE8',
            px: 0.8,
            py: 0.25,
            borderRadius: '5px',
          }}
        >
          {item.label}
        </Typography>
        {onCollapse && (
          <Tooltip title="Collapse sidebar">
            <IconButton size="small" onClick={onCollapse} sx={{ color: 'grey.700', '&:hover': { color: 'grey.900' } }}>
              <MdChevronLeft size={16} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <TreeView
        aria-label={`${item.label} navigation`}
        expanded={expanded}
        onNodeToggle={(_event, nodeIds) => setExpanded(nodeIds)}
        // Folder as the expand indicator: filled+open when expanded, outline
        // closed when collapsed (replaces the arrow chevrons).
        defaultCollapseIcon={<RiFolderOpenFill size={16} />}
        defaultExpandIcon={<RiFolderLine size={16} />}
        defaultEndIcon={null}
        sx={{
          '& .MuiTreeItem-group': {
            ml: 1.5,
            pl: `${CONNECTOR_WIDTH}px`,
            borderLeft: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {children.map(subItem => renderTreeItem(subItem))}
      </TreeView>
    </Box>
  );
};
